import React, { useCallback, useEffect, useRef, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import type { StyledProps } from '@compiled/react';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { Box } from '@atlaskit/primitives/compiled/box';
import { cssMap } from '@atlaskit/css';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Button from '@atlaskit/button/default/button';
import Image from '@atlaskit/image/image';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import imgScalability from '../../../img/scalability.svg';
import imgSuccess from '../../../img/success.svg';
import { useGetSelectedDocuments } from '../../../hooks/useGetSelectedDocuments.ts';
import { isBulkUpload } from '../../../utils/isBulkUpload.ts';
import { useHandleProgressBar } from '../../../hooks/useHandleProgressBar.ts';
import { i18n as retryMessages } from '../BulkLimitModal.tsx';

import { ProgressBulkDocModal } from './ProgressBulkDocModal.tsx';
import { ProgressSingleDocModal } from './ProgressSingleDocModal.tsx';
import { ProgressStatus } from './progress-types.ts';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ProgressText: React.ComponentType<
	React.ClassAttributes<HTMLParagraphElement> &
		React.HTMLAttributes<HTMLParagraphElement> &
		StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
> = styled.p({
	padding: `0 0 ${token('space.300')} 0`,
	display: 'flex',
	justifyContent: 'space-between',
	font: token('font.body'),
	margin: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ProgressTextBulk: React.ComponentType<
	React.ClassAttributes<HTMLParagraphElement> &
		React.HTMLAttributes<HTMLParagraphElement> &
		StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
> = styled(ProgressText)({
	padding: '0 50px 24px 20px',
	justifyContent: 'space-around',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ProgressPercentage: React.ComponentType<
	React.ClassAttributes<HTMLSpanElement> & React.HTMLAttributes<HTMLSpanElement> & StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
> = styled.span({
	minWidth: '40px',
	color: token('color.text'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ImageWrapper: React.ComponentType<
	React.ClassAttributes<HTMLDivElement> & React.HTMLAttributes<HTMLDivElement> & StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
> = styled.div({
	padding: `${token('space.600')} 60px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const IconWrapper: React.ComponentType<
	React.ClassAttributes<HTMLSpanElement> & React.HTMLAttributes<HTMLSpanElement> & StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
> = styled.span({
	marginRight: token('space.100'),
});

const styles = cssMap({
	image: {
		paddingBlock: token('space.600'),
		paddingInline: token('space.800'),
	},
	// The V2 illustration is a 232x232 square rather than the 549x448 landscape
	// artwork, so it is centred at its natural size instead of stretched to the
	// full modal width. No inline padding: the modal body leaves 282px of content
	// width, and adding space.800 either side would squeeze that to 218px, which
	// is narrower than the asset and would let flex-shrink scale it down.
	imageV2: {
		display: 'flex',
		justifyContent: 'center',
		paddingBlock: token('space.600'),
	},
});

export const i18n: {
	uploadModalDialogFinishAction: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	uploadModalDialogCancelAction: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	uploadModalDialogFinishAction: {
		id: 'import-from-sources.upload-modal.finishAction',
		defaultMessage: 'Finish',
		description: 'Finishes the file uploading and closes the upload modal',
	},
	uploadModalDialogCancelAction: {
		id: 'import-from-sources.upload-modal.cancelAction',
		defaultMessage: 'Cancel',
		description: 'Cancels the file uploading and closes the upload modal',
	},
});

const ChoreographerModal = ({ children, onClose, testId }) => {
	const { Modal } = useChoreographerAssets();

	return (
		<Modal onClose={onClose} width={394} testId={testId}>
			{children}
		</Modal>
	);
};

/**
 * How long the completed progress bar is held on screen before auto-landing in
 * the imported doc. Long enough to read as "done", short enough not to feel
 * like a stall.
 */
const COMPLETION_BEAT_MS = 500;

type ProgressModalProps = {
	status: ProgressStatus;
	bulkParentPageId: string | null;
	isDisabledCancel: boolean;
	onCancel: () => void;
	onFinishDraft: () => void;
	onFinishPage: () => void;
	onRetry: () => void;
	isCancelledDocumentsDeleted: boolean;
	/**
	 * Import in Onboarding V2 (TWCG-2304 / SPICE-2553). When enabled the modal
	 * collapses to a single progress bar and navigates into the imported doc as
	 * soon as the import succeeds, rather than parking the user behind a manual
	 * "Finish" click. Resolved by the caller so the Statsig exposure is only
	 * fired once, at the ImportModals level.
	 */
	isAutoLandEnabled?: boolean;
};

export const ProgressModal: React.FC<ProgressModalProps> = ({
	status,
	onCancel,
	onFinishDraft,
	onFinishPage,
	onRetry,
	isDisabledCancel,
	bulkParentPageId,
	isCancelledDocumentsDeleted,
	isAutoLandEnabled = false,
}) => {
	const [progress, setProgress] = useState(0);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { selectedDocuments } = useGetSelectedDocuments();
	const isBulk = isBulkUpload(selectedDocuments);
	useHandleProgressBar(status, progress, setProgress);

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'importProgressModal',
			},
		}).fire();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleDismiss = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'importProgressModal',
				action: 'dismissed',
				actionSubject: 'modal',
				actionSubjectId: 'importProgressModal',
			},
		}).fire();
		onCancel();
	};

	// `action` stays 'clicked' even when the import auto-lands. The event is the
	// funnel's success marker and Phase 0 (SPICE-2548) mapped it to a core action,
	// so changing the shape here would split the metric across the V1/V2 cohorts.
	// `trigger` distinguishes the two paths for anyone slicing the data.
	const handleAnalyticsFinish = useCallback(
		(trigger: 'click' | 'auto') => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'importSuccessModal',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'importSuccessFinish',
					attributes: {
						trigger,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const finishHandler = () => {
		handleAnalyticsFinish('click');
		onFinishDraft();
	};

	const finishHandlerBulk = () => {
		handleAnalyticsFinish('click');
		onFinishPage();
	};

	// Auto-land: on success, navigate straight into the imported doc instead of
	// waiting for a "Finish" click. Ref-guarded because `status` can settle on
	// SUCCESS across several renders and navigating twice would double-fire the
	// analytics event and race the router.
	const hasAutoLandedRef = useRef(false);
	const onFinishDraftRef = useRef(onFinishDraft);
	const onFinishPageRef = useRef(onFinishPage);
	onFinishDraftRef.current = onFinishDraft;
	onFinishPageRef.current = onFinishPage;

	useEffect(() => {
		if (!isAutoLandEnabled || status !== ProgressStatus.SUCCESS || hasAutoLandedRef.current) {
			return;
		}
		hasAutoLandedRef.current = true;
		handleAnalyticsFinish('auto');
		// Mirrors getFinishAction(): bulk imports land on the created parent page,
		// single imports land on the created draft.
		const land = () => {
			if (isBulk) {
				onFinishPageRef.current();
			} else {
				onFinishDraftRef.current();
			}
		};

		// The progress bar is simulated and caps at 99 until SUCCESS flips it to
		// 100 (see useHandleProgressBar). Navigating in the same commit unmounts
		// the modal before that final frame paints, so the bar appears to stop
		// short. This beat lets it visibly complete before we land in the doc.
		const timer = setTimeout(land, COMPLETION_BEAT_MS);
		return () => clearTimeout(timer);
	}, [isAutoLandEnabled, status, isBulk, handleAnalyticsFinish]);

	const cancelHandler = () => {
		if (status === ProgressStatus.IN_PROGRESS) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'importProgressModal',
					action: 'dismissed',
					actionSubject: 'button',
					actionSubjectId: 'importInProgressCancel',
				},
			}).fire();
		}
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'importSuccessModal',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'importCancel',
			},
		}).fire();
		return onCancel();
	};

	const getFinishAction = () => {
		if (status === ProgressStatus.ERROR) return onRetry;
		return isBulk ? finishHandlerBulk : finishHandler;
	};

	return (
		<ChoreographerModal onClose={handleDismiss} testId="import-progress-modal">
			{/* eslint-disable-next-line @atlaskit/design-system/use-modal-title */}
			<ModalHeader hasCloseButton />
			<ModalBody>
				<Box xcss={isAutoLandEnabled ? styles.imageV2 : styles.image}>
					{isAutoLandEnabled ? (
						<Image alt="" src={imgScalability} width="232" height="232" />
					) : (
						<Image alt="" src={imgSuccess} width="100%" />
					)}
				</Box>
				{isBulk ? (
					<ProgressBulkDocModal
						status={status}
						progress={progress}
						docs={selectedDocuments}
						bulkParentPageId={bulkParentPageId}
						isCancelledDocumentsDeleted={isCancelledDocumentsDeleted}
						useSingleProgressBar={isAutoLandEnabled}
					/>
				) : (
					<ProgressSingleDocModal status={status} progress={progress} />
				)}
			</ModalBody>
			{/*
			 * The V2 design drops the footer entirely: success auto-lands in the doc,
			 * "Finish" is a dead step, and dismissal is handled by the header close
			 * button. The footer is still rendered on ERROR, because that is the only
			 * route back to a Retry.
			 */}
			{(!isAutoLandEnabled || status === ProgressStatus.ERROR) && (
				<ModalFooter>
					<>
						<Button
							key="cancel"
							testId="progress-modal-cancel-button"
							appearance="subtle"
							isDisabled={isDisabledCancel}
							onClick={cancelHandler}
						>
							<FormattedMessage {...i18n.uploadModalDialogCancelAction} />
						</Button>
						<Button
							key="finish"
							testId="progress-modal-finish-button"
							appearance="primary"
							onClick={getFinishAction()}
							isDisabled={status === ProgressStatus.IN_PROGRESS}
						>
							{status === ProgressStatus.ERROR ? (
								<FormattedMessage {...retryMessages.bulkLimitModalDialogRetryAction} />
							) : (
								<FormattedMessage {...i18n.uploadModalDialogFinishAction} />
							)}
						</Button>
					</>
				</ModalFooter>
			)}
		</ChoreographerModal>
	);
};
